import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Rating,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import api from '@services/api';
import ContestCatalogExperience from '@components/contest/ContestCatalogExperience';
import {
  brandColors,
  getContestGenderAccent,
  getGradeColor,
  getGradeTierLabel,
} from '@config/brandTokens';
import {
  CONTEST_AGE_CATEGORIES,
  CONTEST_CAPABILITY_LEVELS,
  CONTEST_CATEGORY_OPTIONS,
  CONTEST_GENDER_KEYS,
  CONTEST_SECTION_ORDER,
  DEFAULT_CAPABILITY_LEVEL,
  formatContestVideoNumber,
  getContestGenderKeys,
  getContestGenderLabel,
  getContestTypeAdminLabel,
  getSportContestCatalog,
  hydrateSectionBlocks,
  compactSectionBlocks,
  legacyFromSectionBlocks,
  newContestSectionBlock,
  sectionBlocksHaveContent,
  normalizeCapabilityLevel,
  getVisibleFrame2Sections,
  getFrame2SectionVisibleIndex,
  isRequiredFrame2Section,
} from '@utils/contestCatalog';
import type {
  ContestAgeSectionContent,
  ContestCapabilityLevel,
  ContestCatalogEntry,
  ContestGenderKey,
  ContestMediaAgeVariant,
  ContestMediaItem,
  ContestRelatedVideo,
  ContestSection,
  ContestSectionBlock,
  ContestSectionKey,
} from '../../types/contestCatalog';
import CapabilityLevelChip from '@components/contest/CapabilityLevelChip';
import AdminFrame2BlockEditor from '@components/admin/AdminFrame2BlockEditor';

interface CategoryDefinition {
  contestType: string;
  id: string;
  label: string;
  description?: string;
  colorToken?: string;
  targetType: 'skill' | 'drill' | 'fitness';
  sortOrder: number;
  isActive: boolean;
}

interface GradeDefinition {
  code: string;
  label: string;
  description?: string;
  colorToken?: string;
  sortOrder?: number;
  isActive?: boolean;
}

interface EnabledGrade {
  gradeCode: string;
  labelOverride?: string;
  displayOrder?: number;
  isEnabled?: boolean;
}

interface BenchmarkRow {
  ageCategory: string;
  gradeCode: string;
  /** all = legacy shared; boys/girls = dual gender targets */
  genderKey: 'all' | ContestGenderKey;
  metricKind: 'target_score' | 'attempt_ratio' | 'composite';
  metricType: string;
  unitLabel: string;
  minValue: number | '';
  maxValue: number | '';
  totalAttempts: number | '';
  minPassed: number | '';
}

interface AgeLimitRow {
  ageCategory: string;
  genderKey: 'all' | ContestGenderKey;
  recommendedMaxTime: number | '';
  timeUnitLabel: string;
  metricUnitLabel: string;
}

export interface CatalogSkill {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  description?: string;
  category: string;
  difficultyLevel?: number | null;
  capabilityLevel?: ContestCapabilityLevel;
  scope: 'global' | 'sport_specific';
  sport?: string | null;
  sortOrder?: number;
  isActive: boolean;
  enabledGrades?: EnabledGrade[];
  benchmarks?: BenchmarkRow[];
  ageLimits?: AgeLimitRow[];
  sportLink?: { contestTaskId?: string | null } | null;
  commonMistakes?: string[];
  sections?: ContestSection[] | null;
  media?: ContestMediaItem[] | null;
}

interface AdminContestCatalogProps {
  sport: string;
}

const CONTEST_TYPE_ID = 'foundation' as const;

const FALLBACK_CATEGORIES = CONTEST_CATEGORY_OPTIONS.map((c, i) => ({
  contestType: CONTEST_TYPE_ID,
  id: c.id,
  label: c.label,
  targetType: (c.id === 'drill' ? 'drill' : c.id === 'fitness' ? 'fitness' : 'skill') as CategoryDefinition['targetType'],
  sortOrder: i + 1,
  isActive: true,
}));

const gradeRowChanged = (original: GradeDefinition, draft: GradeDefinition): boolean =>
  String(draft.code ?? '').trim() !== original.code ||
  (draft.label || '').trim() !== (original.label || '').trim() ||
  (draft.description || '') !== (original.description || '') ||
  (draft.sortOrder ?? 0) !== (original.sortOrder ?? 0) ||
  (draft.isActive !== false) !== (original.isActive !== false);

const emptyAgeLimits = (): AgeLimitRow[] =>
  CONTEST_AGE_CATEGORIES.flatMap((age) =>
    getContestGenderKeys(age).map((genderKey) => ({
      ageCategory: age,
      genderKey,
      recommendedMaxTime: '' as const,
      timeUnitLabel: 'min',
      metricUnitLabel: '',
    })),
  );

const ageLimitKey = (age: string, genderKey: string) => `${age}::${genderKey}`;

const buildAgeLimitsFromSkill = (
  limits: AgeLimitRow[] | undefined,
  benchmarkRows: BenchmarkRow[],
): AgeLimitRow[] => {
  const map = new Map<string, AgeLimitRow>(
    emptyAgeLimits().map((row) => [ageLimitKey(row.ageCategory, row.genderKey), { ...row }]),
  );
  (limits || []).forEach((row) => {
    const raw = String(row.genderKey || 'all').trim().toLowerCase();
    const g: AgeLimitRow['genderKey'] | 'all' =
      raw === 'girls' || raw === 'girl' || raw === 'female' || raw === 'f'
        ? 'girls'
        : raw === 'boys' || raw === 'boy' || raw === 'male' || raw === 'm'
          ? 'boys'
          : raw === 'open'
            ? 'open'
            : raw === 'diverse' || raw === 'gender_diverse' || raw === 'other'
              ? 'diverse'
              : 'all';
    if (g === 'all') {
      // Legacy shared limit seeds the base Boy/Girl rows
      CONTEST_GENDER_KEYS.forEach((genderKey) => {
        map.set(ageLimitKey(row.ageCategory, genderKey), {
          ageCategory: row.ageCategory,
          genderKey,
          recommendedMaxTime: row.recommendedMaxTime ?? '',
          timeUnitLabel: row.timeUnitLabel || 'min',
          metricUnitLabel: row.metricUnitLabel || '',
        });
      });
      return;
    }
    map.set(ageLimitKey(row.ageCategory, g), {
      ageCategory: row.ageCategory,
      genderKey: g,
      recommendedMaxTime: row.recommendedMaxTime ?? '',
      timeUnitLabel: row.timeUnitLabel || 'min',
      metricUnitLabel: row.metricUnitLabel || '',
    });
  });
  CONTEST_AGE_CATEGORIES.forEach((age) => {
    getContestGenderKeys(age).forEach((genderKey) => {
      const existing = map.get(ageLimitKey(age, genderKey));
      if (existing && !existing.metricUnitLabel) {
        const sample = benchmarkRows.find(
          (b) =>
            b.ageCategory === age &&
            b.unitLabel &&
            ((b.genderKey || 'all') === genderKey || (b.genderKey || 'all') === 'all'),
        );
        if (sample?.unitLabel) existing.metricUnitLabel = sample.unitLabel;
      }
    });
  });
  return Array.from(map.values());
};

function emptyAgeSection(
  from?: ContestAgeSectionContent,
  fallback?: { content?: string; bullets?: string[]; blocks?: ContestSectionBlock[] },
): ContestAgeSectionContent {
  const content = from?.content ?? fallback?.content ?? '';
  const bullets = from?.bullets ? [...from.bullets] : fallback?.bullets ? [...fallback.bullets] : [];
  const blocks = hydrateSectionBlocks(from?.blocks?.length ? from : { content, bullets, blocks: fallback?.blocks });
  return {
    content,
    bullets,
    blocks: blocks.length ? blocks : [newContestSectionBlock()],
  };
}

function hydrateSectionByAge(section: ContestSection): Record<string, ContestAgeSectionContent> {
  const shared = {
    content: section.content || '',
    bullets: section.bullets || [],
    blocks: section.blocks,
  };
  const byAge: Record<string, ContestAgeSectionContent> = {};
  CONTEST_AGE_CATEGORIES.forEach((age) => {
    if (section.byAge?.[age]) {
      byAge[age] = emptyAgeSection(section.byAge[age], shared);
    } else {
      byAge[age] = { content: '', bullets: [], blocks: [] };
    }
  });
  return byAge;
}

function setAgeSection(
  section: ContestSection,
  age: string,
  patch: Partial<ContestAgeSectionContent>,
): ContestSection {
  const byAge = hydrateSectionByAge(section);
  const current = byAge[age];
  const hasOwn = sectionBlocksHaveContent(current.blocks) || String(current.content || '').trim();
  const base = hasOwn
    ? emptyAgeSection(current, { content: section.content, bullets: section.bullets, blocks: section.blocks })
    : emptyAgeSection(undefined, { content: section.content, bullets: section.bullets, blocks: section.blocks });
  const next = { ...base, ...patch };
  if (next.blocks) {
    const legacy = legacyFromSectionBlocks(next.blocks);
    next.content = legacy.content;
    next.bullets = legacy.bullets;
  }
  byAge[age] = next;
  return {
    ...section,
    byAge,
    ...(age === 'U13'
      ? { content: next.content, bullets: next.bullets, blocks: next.blocks }
      : {}),
  };
}

function ageClip(
  item: { title?: string; url?: string; notes?: string; byAge?: Record<string, ContestMediaAgeVariant> },
  age: string,
): { title: string; url: string; notes: string } {
  const variant = item.byAge?.[age];
  return {
    title: variant?.title ?? item.title ?? '',
    url: variant?.url ?? item.url ?? '',
    notes: variant?.notes ?? item.notes ?? '',
  };
}

function setAgeClip<T extends { title?: string; url?: string; notes?: string; byAge?: Record<string, ContestMediaAgeVariant> }>(
  item: T,
  age: string,
  patch: Partial<{ title: string; url: string; notes: string }>,
): T {
  const current = ageClip(item, age);
  const next = { ...current, ...patch };
  const byAge = {
    ...(item.byAge || {}),
    [age]: {
      ...(item.byAge?.[age] || {}),
      title: next.title,
      url: next.url,
      notes: next.notes,
    },
  };
  const synced = age === 'U13';
  return {
    ...item,
    byAge,
    ...(synced
      ? { title: next.title, url: next.url, notes: next.notes }
      : {}),
  };
}

function hydrateMediaByAge(item: ContestMediaItem): ContestMediaItem {
  const byAge: NonNullable<ContestMediaItem['byAge']> = { ...(item.byAge || {}) };
  CONTEST_AGE_CATEGORIES.forEach((age) => {
    const existing = byAge[age];
    if (!existing || (!existing.url && !existing.title)) {
      byAge[age] = {
        url: existing?.url || item.url || '',
        title: existing?.title || item.title || '',
        notes: existing?.notes ?? item.notes,
        relatedVideos: existing?.relatedVideos,
      };
    }
  });
  return {
    ...item,
    relatedVideos: (item.relatedVideos || []).map((related) => {
      const relatedByAge: NonNullable<ContestRelatedVideo['byAge']> = { ...(related.byAge || {}) };
      CONTEST_AGE_CATEGORIES.forEach((age) => {
        const existing = relatedByAge[age];
        if (!existing || (!existing.url && !existing.title)) {
          relatedByAge[age] = {
            url: existing?.url || related.url || '',
            title: existing?.title || related.title || '',
            notes: existing?.notes ?? related.notes,
          };
        }
      });
      return { ...related, byAge: relatedByAge };
    }),
    byAge,
  };
}

function compactSectionByAge(
  byAge: Record<string, ContestAgeSectionContent> | undefined,
): Record<string, ContestAgeSectionContent> | undefined {
  if (!byAge) return undefined;
  const out: Record<string, ContestAgeSectionContent> = {};
  Object.entries(byAge).forEach(([age, block]) => {
    const content = String(block?.content || '').trim();
    const bullets = (block?.bullets || []).map((bullet) => String(bullet || '').trim()).filter(Boolean);
    const blocks = compactSectionBlocks(block?.blocks);
    if (content || bullets.length || blocks) out[age] = { content, bullets, blocks };
  });
  return Object.keys(out).length ? out : undefined;
}

function compactMediaByAge(
  byAge: ContestMediaItem['byAge'],
): ContestMediaItem['byAge'] {
  if (!byAge) return undefined;
  const out: NonNullable<ContestMediaItem['byAge']> = {};
  Object.entries(byAge).forEach(([age, block]) => {
    const url = String(block?.url || '').trim();
    const title = String(block?.title || '').trim();
    const notes = String(block?.notes || '').trim();
    if (url || title || notes) {
      out[age] = { url, title: title || undefined, notes: notes || undefined, relatedVideos: block?.relatedVideos };
    }
  });
  return Object.keys(out).length ? out : undefined;
}

function emptyLearnSections(): ContestSection[] {
  return CONTEST_SECTION_ORDER.map(({ key, label }) => ({
    key,
    label,
    content: '',
    bullets: [],
    blocks: [newContestSectionBlock()],
    hidden: false,
    byAge: hydrateSectionByAge({ key, label, content: '', bullets: [], blocks: [newContestSectionBlock()] }),
  }));
}

function findSeedSkill(sport: string, slug?: string): ContestCatalogEntry | undefined {
  if (!slug) return undefined;
  const seed = getSportContestCatalog(sport);
  if (!seed) return undefined;
  for (const typeGroup of seed.contestTypes) {
    for (const cat of typeGroup.categories) {
      const found = cat.contests.find((c) => c.id === slug);
      if (found) return found;
    }
  }
  return undefined;
}

function hydrateLearnSections(skill: CatalogSkill, seed?: ContestCatalogEntry): ContestSection[] {
  const source = Array.isArray(skill.sections) ? skill.sections : seed?.sections;
  const byKey = new Map((source || []).map((s) => [s.key, s]));
  return CONTEST_SECTION_ORDER.map(({ key, label }) => {
    const existing = byKey.get(key);
    return {
      key,
      label: existing?.label || label,
      content: existing?.content || '',
      bullets: existing?.bullets ? [...existing.bullets] : [],
      hidden: isRequiredFrame2Section(key) ? false : Boolean(existing?.hidden),
      blocks: hydrateSectionBlocks({
        content: existing?.content || '',
        bullets: existing?.bullets ? [...existing.bullets] : [],
        blocks: existing?.blocks,
      }),
      byAge: hydrateSectionByAge({
        key,
        label: existing?.label || label,
        content: existing?.content || '',
        bullets: existing?.bullets ? [...existing.bullets] : [],
        blocks: existing?.blocks,
        byAge: existing?.byAge,
      }),
    };
  });
}

function hydrateLearnMedia(skill: CatalogSkill, seed?: ContestCatalogEntry): ContestMediaItem[] {
  const source = Array.isArray(skill.media) ? skill.media : seed?.media || [];
  return source.map((item) => hydrateMediaByAge({ ...item }));
}

function newMediaItem(sectionKey: ContestSectionKey = 'liability_disclaimer'): ContestMediaItem {
  return hydrateMediaByAge({
    id: `media-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: '',
    url: '',
    sectionKey,
    role: 'primary',
    notes: '',
    relatedVideos: [],
  });
}

function newRelatedVideo(): ContestRelatedVideo {
  const related: ContestRelatedVideo = {
    id: `rel-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: '',
    url: '',
    notes: '',
  };
  const byAge: NonNullable<ContestRelatedVideo['byAge']> = {};
  CONTEST_AGE_CATEGORIES.forEach((age) => {
    byAge[age] = { url: '', title: '', notes: '' };
  });
  return { ...related, byAge };
}

const AGE_MATRIX_TABLE_SX = {
  width: '100%',
  minWidth: 720,
  borderCollapse: 'collapse',
  '& th, & td': { border: '1px solid #e2e8f0', p: 0.75, fontSize: '0.85rem', verticalAlign: 'top' },
  '& th': { bgcolor: '#f1f5f9', fontWeight: 700 },
} as const;

const AgeClipTable: React.FC<{
  item: Pick<ContestMediaItem, 'title' | 'url' | 'notes' | 'byAge'> | ContestRelatedVideo;
  onChangeAge: (age: string, patch: Partial<{ title: string; url: string; notes: string }>) => void;
}> = ({ item, onChangeAge }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Box component="table" sx={AGE_MATRIX_TABLE_SX}>
      <thead>
        <tr>
          <th>Age</th>
          <th>Title</th>
          <th>Video URL</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        {CONTEST_AGE_CATEGORIES.map((age) => {
          const clip = ageClip(item, age);
          return (
            <tr key={age}>
              <td style={{ width: 88 }}>
                <strong>{age}</strong>
              </td>
              <td>
                <TextField
                  size="small"
                  fullWidth
                  hiddenLabel
                  placeholder={`${age} title`}
                  value={clip.title}
                  onChange={(e) => onChangeAge(age, { title: e.target.value })}
                />
              </td>
              <td>
                <TextField
                  size="small"
                  fullWidth
                  hiddenLabel
                  placeholder={`${age} video URL`}
                  value={clip.url}
                  onChange={(e) => onChangeAge(age, { url: e.target.value })}
                />
              </td>
              <td>
                <TextField
                  size="small"
                  fullWidth
                  hiddenLabel
                  placeholder={`${age} notes`}
                  value={clip.notes}
                  onChange={(e) => onChangeAge(age, { notes: e.target.value })}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </Box>
  </Box>
);

const OptionChipList: React.FC<{
  label: string;
  helperText?: string;
  values: string[];
  onChange: (next: string[]) => void;
}> = ({ label, helperText, values, onChange }) => {
  const [input, setInput] = useState('');
  const add = () => {
    const text = input.trim();
    if (!text) return;
    if (values.some((value) => value.toLowerCase() === text.toLowerCase())) {
      setInput('');
      return;
    }
    onChange([...values, text]);
    setInput('');
  };
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {label}
      </Typography>
      {helperText ? (
        <Typography variant="caption" sx={{ color: brandColors.slate, display: 'block', mb: 1 }}>
          {helperText}
        </Typography>
      ) : null}
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          fullWidth
          size="small"
          label="Add common mistake"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button variant="outlined" onClick={add} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
          Add
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {values.map((option) => (
          <Chip
            key={option}
            label={option}
            size="small"
            onDelete={() => onChange(values.filter((value) => value !== option))}
          />
        ))}
      </Box>
    </Box>
  );
};

const emptySkillDraft = (sport: string, defaultCategory = 'skill'): Partial<CatalogSkill> => ({
  title: '',
  subtitle: '',
  description: '',
  category: defaultCategory,
  difficultyLevel: 1,
  capabilityLevel: DEFAULT_CAPABILITY_LEVEL,
  scope: 'sport_specific',
  sport,
  sortOrder: 0,
  isActive: true,
  commonMistakes: [],
  sections: emptyLearnSections(),
  media: [],
});

/** Admin editor for Developmental & Performance Contest skills + benchmarks (per sport). */
const AdminContestCatalog: React.FC<AdminContestCatalogProps> = ({ sport }) => {
  const resolvedSport = sport === 'All Sports' ? 'Badminton' : sport;

  const [mainTab, setMainTab] = useState<'edit' | 'categories' | 'grades' | 'preview'>('edit');
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const [skills, setSkills] = useState<CatalogSkill[]>([]);
  const [categories, setCategories] = useState<CategoryDefinition[]>([]);
  const [categoryDrafts, setCategoryDrafts] = useState<Record<string, CategoryDefinition>>({});
  const [grades, setGrades] = useState<GradeDefinition[]>([]);
  const [gradeDrafts, setGradeDrafts] = useState<Record<string, GradeDefinition>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<CatalogSkill> | null>(null);
  const [enabledGrades, setEnabledGrades] = useState<string[]>([]);
  const [benchmarks, setBenchmarks] = useState<BenchmarkRow[]>([]);
  const [ageLimits, setAgeLimits] = useState<AgeLimitRow[]>(emptyAgeLimits());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingGrades, setSavingGrades] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [categoryCreateOpen, setCategoryCreateOpen] = useState(false);
  const [gradeCreateOpen, setGradeCreateOpen] = useState(false);
  const [gradeToDelete, setGradeToDelete] = useState<GradeDefinition | null>(null);
  const [gradeDeleteError, setGradeDeleteError] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState({
    id: '',
    label: '',
    description: '',
    targetType: 'skill' as CategoryDefinition['targetType'],
    sortOrder: 0,
  });
  const [newGrade, setNewGrade] = useState({
    code: '',
    label: '',
    description: '',
    sortOrder: 0,
  });
  const [newSkill, setNewSkill] = useState<Partial<CatalogSkill>>(emptySkillDraft(resolvedSport));
  const [skillEditorTab, setSkillEditorTab] = useState<'frame1' | 'frame2' | 'frame3'>('frame1');
  const [frame2Age, setFrame2Age] = useState('U13');
  const [frame3SectionKey, setFrame3SectionKey] = useState<ContestSectionKey>('liability_disclaimer');

  const activeGrades = useMemo(
    () =>
      [...grades]
        .filter((g) => g.isActive !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.code.localeCompare(b.code)),
    [grades],
  );

  const gradesDirty = useMemo(
    () =>
      grades.some((g) => {
        const draft = gradeDrafts[g.code] || g;
        return gradeRowChanged(g, draft);
      }),
    [grades, gradeDrafts],
  );

  const activeCategories = useMemo(
    () => categories.filter((c) => c.isActive !== false),
    [categories],
  );

  const categoryLabelById = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((c) => map.set(c.id, c.label));
    return map;
  }, [categories]);

  const selectedSkill = useMemo(
    () => skills.find((s) => s.id === selectedId) ?? null,
    [skills, selectedId],
  );

  const loadCategories = useCallback(async () => {
    try {
      const res = await api.get('/admin/contest-catalog/categories', {
        params: { contestType: CONTEST_TYPE_ID, includeInactive: true },
      });
      const rows: CategoryDefinition[] = Array.isArray(res.data?.data) ? res.data.data : [];
      setCategories(rows.length ? rows : FALLBACK_CATEGORIES);
      setCategoryDrafts(
        Object.fromEntries((rows.length ? rows : FALLBACK_CATEGORIES).map((c) => [c.id, { ...c }])),
      );
    } catch {
      setCategories(FALLBACK_CATEGORIES);
      setCategoryDrafts(Object.fromEntries(FALLBACK_CATEGORIES.map((c) => [c.id, { ...c }])));
    }
  }, []);

  const loadGrades = useCallback(async () => {
    try {
      const res = await api.get('/admin/contest-catalog/grades', {
        params: { includeInactive: true },
      });
      const rows: GradeDefinition[] = Array.isArray(res.data?.data) ? res.data.data : [];
      setGrades(rows);
      setGradeDrafts(Object.fromEntries(rows.map((g) => [g.code, { ...g, code: g.code }])));
    } catch (e: unknown) {
      setGrades([]);
      setGradeDrafts({});
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        'Failed to load platform grades';
      setError(msg);
    }
  }, []);

  const loadSkills = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/admin/contest-catalog/${encodeURIComponent(resolvedSport)}/skills`);
      const rows: CatalogSkill[] = Array.isArray(res.data?.data) ? res.data.data : [];
      setSkills(rows);
      if (rows.length && !selectedId) {
        setSelectedId(rows[0].id);
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        'Failed to load catalog';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [resolvedSport, selectedId]);

  useEffect(() => {
    loadGrades();
    loadCategories();
    loadSkills();
  }, [resolvedSport]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mainTab === 'preview') {
      setPreviewRefreshKey((k) => k + 1);
    }
  }, [mainTab]);

  useEffect(() => {
    if (mainTab === 'grades') {
      loadGrades();
    }
  }, [mainTab, loadGrades]);

  useEffect(() => {
    if (activeCategories.length && !activeCategories.some((c) => c.id === newSkill.category)) {
      setNewSkill((prev) => ({ ...prev, category: activeCategories[0].id }));
    }
  }, [activeCategories, newSkill.category]);

  const defaultEnabledGradeCodes = useMemo(
    () => activeGrades.map((g) => g.code),
    [activeGrades],
  );

  const orderedEnabledGrades = useMemo(() => {
    const order = new Map(activeGrades.map((g, i) => [g.code, g.sortOrder ?? i]));
    return [...enabledGrades].sort(
      (a, b) => (order.get(a) ?? 999) - (order.get(b) ?? 999) || a.localeCompare(b),
    );
  }, [enabledGrades, activeGrades]);

  useEffect(() => {
    if (!selectedSkill) {
      setDraft(null);
      setBenchmarks([]);
      setAgeLimits(emptyAgeLimits());
      setEnabledGrades(defaultEnabledGradeCodes);
      return;
    }
    const seed = findSeedSkill(resolvedSport, selectedSkill.slug);
    setSkillEditorTab('frame1');
    setFrame3SectionKey('liability_disclaimer');
    setDraft({
      ...selectedSkill,
      commonMistakes: selectedSkill.commonMistakes || [],
      sections: hydrateLearnSections(selectedSkill, seed),
      media: hydrateLearnMedia(selectedSkill, seed),
    });
    const enabled = (selectedSkill.enabledGrades || [])
      .filter((g) => g.isEnabled !== false)
      .map((g) => g.gradeCode);
    const order = new Map(activeGrades.map((g, i) => [g.code, g.sortOrder ?? i]));
    const nextEnabled = enabled.length ? enabled : defaultEnabledGradeCodes;
    setEnabledGrades(
      [...nextEnabled].sort(
        (a, b) => (order.get(a) ?? 999) - (order.get(b) ?? 999) || a.localeCompare(b),
      ),
    );
    const benchRows = (selectedSkill.benchmarks || []).map((b) => ({
      ...b,
      genderKey: (b.genderKey as BenchmarkRow['genderKey']) || 'all',
      minValue: b.minValue ?? '',
      maxValue: b.maxValue ?? '',
      totalAttempts: b.totalAttempts ?? '',
      minPassed: b.minPassed ?? '',
      unitLabel: b.unitLabel || '',
      metricKind: (b.metricKind as BenchmarkRow['metricKind']) || 'target_score',
      metricType: b.metricType || 'count',
    }));
    setBenchmarks(benchRows);
    setAgeLimits(buildAgeLimitsFromSkill(selectedSkill.ageLimits, benchRows));
  }, [selectedSkill, defaultEnabledGradeCodes, activeGrades]);

  const getAgeLimitRow = (age: string, genderKey: ContestGenderKey) =>
    ageLimits.find((r) => r.ageCategory === age && r.genderKey === genderKey);

  const syncAgeLimit = (age: string, genderKey: ContestGenderKey, patch: Partial<AgeLimitRow>) => {
    setAgeLimits((prev) =>
      prev.map((row) =>
        row.ageCategory === age && row.genderKey === genderKey ? { ...row, ...patch } : row,
      ),
    );
    if (patch.metricUnitLabel != null) {
      setBenchmarks((prev) =>
        prev.map((b) =>
          b.ageCategory === age &&
          ((b.genderKey || 'all') === genderKey || (b.genderKey || 'all') === 'all')
            ? { ...b, unitLabel: patch.metricUnitLabel || '' }
            : b,
        ),
      );
    }
  };

  const syncBenchmarkCell = (
    age: string,
    gradeCode: string,
    genderKey: ContestGenderKey,
    patch: Partial<BenchmarkRow>,
  ) => {
    setBenchmarks((prev) => {
      const idx = prev.findIndex(
        (b) =>
          b.ageCategory === age &&
          b.gradeCode === gradeCode &&
          (b.genderKey || 'all') === genderKey,
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], genderKey, ...patch };
        return next;
      }
      // Seed from legacy shared ("all") cell when first editing a gender row
      const shared = prev.find(
        (b) =>
          b.ageCategory === age &&
          b.gradeCode === gradeCode &&
          (b.genderKey || 'all') === 'all',
      );
      return [
        ...prev,
        {
          ageCategory: age,
          gradeCode,
          genderKey,
          metricKind: 'target_score',
          metricType: 'count',
          unitLabel: '',
          minValue: shared?.minValue ?? '',
          maxValue: shared?.maxValue ?? '',
          totalAttempts: '',
          minPassed: '',
          ...patch,
        },
      ];
    });
  };

  const getBenchmarkCell = (age: string, gradeCode: string, genderKey: ContestGenderKey) => {
    const gendered = benchmarks.find(
      (b) =>
        b.ageCategory === age &&
        b.gradeCode === gradeCode &&
        (b.genderKey || 'all') === genderKey,
    );
    if (gendered) return gendered;
    return benchmarks.find(
      (b) =>
        b.ageCategory === age &&
        b.gradeCode === gradeCode &&
        (b.genderKey || 'all') === 'all',
    );
  };

  const handleSaveCategory = async (categoryId: string) => {
    const draft = categoryDrafts[categoryId];
    if (!draft?.label?.trim()) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.put(
        `/admin/contest-catalog/categories/${encodeURIComponent(CONTEST_TYPE_ID)}/${encodeURIComponent(categoryId)}`,
        {
          label: draft.label.trim(),
          description: draft.description || '',
          targetType: draft.targetType,
          sortOrder: draft.sortOrder ?? 0,
          isActive: draft.isActive !== false,
        },
      );
      setSuccess(`Category "${draft.label}" saved.`);
      await loadCategories();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        'Category save failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.label?.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.post('/admin/contest-catalog/categories', {
        id: newCategory.id.trim() || undefined,
        label: newCategory.label.trim(),
        contestType: CONTEST_TYPE_ID,
        description: newCategory.description || '',
        targetType: newCategory.targetType,
        sortOrder: newCategory.sortOrder ?? categories.length + 1,
      });
      setCategoryCreateOpen(false);
      setNewCategory({ id: '', label: '', description: '', targetType: 'skill', sortOrder: categories.length + 1 });
      setSuccess('Category created.');
      await loadCategories();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        'Create category failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAllGrades = async () => {
    const toSave = grades
      .map((g) => ({ original: g, draft: gradeDrafts[g.code] || g }))
      .filter(({ original, draft }) => gradeRowChanged(original, draft));

    if (!toSave.length) return;

    for (const { draft } of toSave) {
      if (!draft.label?.trim() || !String(draft.code ?? '').trim()) {
        setError('Every grade must have a code and label.');
        return;
      }
    }

    const codes = grades.map((g) => String((gradeDrafts[g.code] || g).code ?? g.code).trim());
    if (new Set(codes).size !== codes.length) {
      setError('Grade codes must be unique.');
      return;
    }

    setSavingGrades(true);
    setError(null);
    setSuccess(null);
    try {
      for (const { original, draft } of toSave) {
        const nextCode = String(draft.code ?? original.code).trim();
        await api.post('/admin/contest-catalog/grades', {
          code: nextCode,
          previousCode: nextCode !== original.code ? original.code : undefined,
          label: draft.label!.trim(),
          description: draft.description || '',
          sortOrder: draft.sortOrder ?? 0,
          isActive: draft.isActive !== false,
        });
      }
      setSuccess(`${toSave.length} grade${toSave.length === 1 ? '' : 's'} saved.`);
      await loadGrades();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        'Grade save failed';
      setError(msg);
    } finally {
      setSavingGrades(false);
    }
  };

  const handleCreateGrade = async () => {
    if (!newGrade.code?.trim() || !newGrade.label?.trim()) return;
    setSavingGrades(true);
    setError(null);
    try {
      await api.post('/admin/contest-catalog/grades', {
        code: newGrade.code.trim(),
        label: newGrade.label.trim(),
        description: newGrade.description || '',
        sortOrder: newGrade.sortOrder ?? grades.length + 1,
        isActive: true,
      });
      setGradeCreateOpen(false);
      setNewGrade({ code: '', label: '', description: '', sortOrder: grades.length + 1 });
      setSuccess('Grade created.');
      await loadGrades();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        'Create grade failed';
      setError(msg);
    } finally {
      setSavingGrades(false);
    }
  };

  const handleDeleteGrade = async (deactivateIfUsed = false) => {
    if (!gradeToDelete?.code) return;
    setSavingGrades(true);
    setError(null);
    setSuccess(null);
    setGradeDeleteError(null);
    try {
      const res = await api.post(
        '/admin/contest-catalog/grades',
        { action: 'remove', code: gradeToDelete.code, deactivateIfUsed },
        { skipErrorToast: true },
      );
      const mode = res.data?.mode === 'deactivated' ? 'hidden (deactivated)' : 'deleted';
      if (res.data?.mode === 'deactivated' && gradeToDelete?.code) {
        const code = gradeToDelete.code;
        setGrades((prev) => prev.map((g) => (g.code === code ? { ...g, isActive: false } : g)));
        setGradeDrafts((prev) => {
          const row = prev[code];
          return row ? { ...prev, [code]: { ...row, isActive: false } } : prev;
        });
      }
      setSuccess(`Grade "${gradeToDelete.code}" ${mode}.`);
      setGradeToDelete(null);
      await loadGrades();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        'Delete grade failed';
      setGradeDeleteError(msg);
    } finally {
      setSavingGrades(false);
    }
  };

  const handleSave = async () => {
    if (!draft?.id || !draft.title?.trim()) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const ageLimitPayload = (() => {
        const byKey = new Map<string, {
          ageCategory: string;
          genderKey: ContestGenderKey;
          recommendedMaxTime: number | null;
          timeUnitLabel: string;
          metricUnitLabel: string | null;
        }>();
        ageLimits.forEach((row) => {
          const hasTime = row.recommendedMaxTime !== '' && row.recommendedMaxTime != null;
          const hasMetric = row.metricUnitLabel.trim() !== '';
          // Do not treat default timeUnitLabel ("min") as content — that caused empty rows to flood the API.
          if (!hasTime && !hasMetric) return;
          const genderKey = (row.genderKey === 'all' ? 'boys' : row.genderKey) as ContestGenderKey;
          byKey.set(ageLimitKey(row.ageCategory, genderKey), {
            ageCategory: row.ageCategory,
            genderKey,
            recommendedMaxTime: hasTime ? Number(row.recommendedMaxTime) : null,
            timeUnitLabel: row.timeUnitLabel || 'min',
            metricUnitLabel: hasMetric ? row.metricUnitLabel.trim() : null,
          });
        });
        return Array.from(byKey.values());
      })();

      const benchmarkPayload = CONTEST_AGE_CATEGORIES.flatMap((age) =>
        getContestGenderKeys(age).flatMap((genderKey) =>
          orderedEnabledGrades.map((gradeCode) => {
            const cell = getBenchmarkCell(age, gradeCode, genderKey);
            if (!cell) return null;
            if (cell.minValue === '' && cell.maxValue === '') return null;
            const ageMetric = getAgeLimitRow(age, genderKey)?.metricUnitLabel?.trim();
            return {
              ageCategory: age,
              gradeCode,
              genderKey,
              metricKind: cell.metricKind || 'target_score',
              metricType: cell.metricType || 'count',
              unitLabel: ageMetric || cell.unitLabel || null,
              minValue: cell.minValue === '' ? null : Number(cell.minValue),
              maxValue: cell.maxValue === '' ? null : Number(cell.maxValue),
              totalAttempts: cell.totalAttempts === '' ? null : Number(cell.totalAttempts),
              minPassed: cell.minPassed === '' ? null : Number(cell.minPassed),
            };
          }),
        ),
      ).filter(Boolean);

      await api.put(`/admin/contest-catalog/skills/${draft.id}`, {
        title: draft.title,
        subtitle: draft.subtitle || '',
        description: draft.description || '',
        category: draft.category,
        difficultyLevel: draft.difficultyLevel ?? null,
        capabilityLevel: normalizeCapabilityLevel(draft.capabilityLevel),
        sortOrder: draft.sortOrder ?? 0,
        isActive: draft.isActive !== false,
        sport: resolvedSport,
        enabledGrades: orderedEnabledGrades.map((code, i) => ({
          gradeCode: code,
          displayOrder: i,
          isEnabled: true,
        })),
        benchmarks: benchmarkPayload,
        ageLimits: ageLimitPayload,
        commonMistakes: draft.commonMistakes || [],
        sections: (draft.sections || emptyLearnSections()).map((section) => {
          const u13 = section.byAge?.U13;
          const blocks = compactSectionBlocks(u13?.blocks ?? section.blocks);
          const legacy = legacyFromSectionBlocks(blocks);
          return {
            key: section.key,
            label: section.label,
            content: legacy.content || String(u13?.content ?? section.content ?? '').trim(),
            bullets: legacy.bullets.length
              ? legacy.bullets
              : (u13?.bullets ?? section.bullets ?? [])
                  .map((bullet) => String(bullet || '').trim())
                  .filter(Boolean),
            hidden: isRequiredFrame2Section(section.key) ? false : Boolean(section.hidden),
            blocks,
            byAge: compactSectionByAge(section.byAge),
          };
        }),
        media: (draft.media || []).map((item, index, all) => {
          const sectionKey = item.sectionKey || 'scoring';
          const sectionIndex = all
            .filter((row) => (row.sectionKey || 'scoring') === sectionKey)
            .findIndex((row) => row.id === item.id);
          const primaryIndex = sectionIndex >= 0 ? sectionIndex : index;
          const u13 = ageClip(item, 'U13');
          const sectionHidden =
            Boolean((draft.sections || []).find((row) => row.key === sectionKey)?.hidden) &&
            !isRequiredFrame2Section(sectionKey);
          const visibleMajor = getFrame2SectionVisibleIndex(
            { sections: draft.sections || [] },
            sectionKey,
          );
          const fallbackTitle = `Video ${
            sectionHidden
              ? formatContestVideoNumber(primaryIndex)
              : formatContestVideoNumber(visibleMajor + primaryIndex)
          }`;
          return {
          ...item,
          id: item.id || `media-${index + 1}`,
          title: String(u13.title || item.title || '').trim() || fallbackTitle,
          url: String(u13.url || item.url || '').trim(),
          sectionKey,
          notes: String(u13.notes || item.notes || '').trim() || undefined,
          byAge: compactMediaByAge(item.byAge),
          relatedVideos: (item.relatedVideos || [])
            .map((related, relatedIndex) => {
              const relatedClip = ageClip(related, 'U13');
              const relatedFallback = `Video ${
                sectionHidden
                  ? formatContestVideoNumber(primaryIndex, relatedIndex)
                  : formatContestVideoNumber(visibleMajor + primaryIndex, relatedIndex)
              }`;
              const compacted = compactMediaByAge(related.byAge);
              return {
                ...related,
                id: related.id || `rel-${index + 1}-${relatedIndex + 1}`,
                title: String(relatedClip.title || related.title || '').trim() || relatedFallback,
                url: String(relatedClip.url || related.url || '').trim(),
                notes: String(relatedClip.notes || related.notes || '').trim() || undefined,
                byAge: compacted,
              };
            })
            .filter(
              (related) =>
                related.url ||
                related.title ||
                (related.byAge && Object.values(related.byAge).some((block) => block?.url || block?.title)),
            ),
        };
        }),
      });
      setSuccess('Skill saved.');
      setPreviewRefreshKey((k) => k + 1);
      await loadSkills();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        'Save failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!newSkill.title?.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.post('/admin/contest-catalog/skills', {
        title: newSkill.title.trim(),
        subtitle: newSkill.subtitle || '',
        description: newSkill.description || '',
        category: newSkill.category || 'skill',
        difficultyLevel: newSkill.difficultyLevel ?? 1,
        capabilityLevel: normalizeCapabilityLevel(newSkill.capabilityLevel),
        scope: newSkill.scope || 'sport_specific',
        sport: newSkill.scope === 'global' ? null : resolvedSport,
        adoptSport: newSkill.scope === 'global' ? resolvedSport : undefined,
        sortOrder: newSkill.sortOrder ?? skills.length,
        enabledGrades: defaultEnabledGradeCodes.map((code) => ({ gradeCode: code })),
        commonMistakes: newSkill.commonMistakes || [],
      });
      setCreateOpen(false);
      setNewSkill(emptySkillDraft(resolvedSport));
      setSuccess('Skill created with linked contest task.');
      await loadSkills();
      if (res.data?.data?.id) setSelectedId(res.data.data.id);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        'Create failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleAdoptGlobal = async (skillId: string) => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/admin/contest-catalog/skills/${skillId}/adopt`, { sport: resolvedSport });
      setSuccess('Global skill adopted on this sport.');
      await loadSkills();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error)?.message ||
        'Adopt failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const contestTypeLabel = getContestTypeAdminLabel(CONTEST_TYPE_ID);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: brandColors.navy }}>
            {resolvedSport} — {contestTypeLabel}
          </Typography>
          <Typography variant="body2" sx={{ color: brandColors.slate, mt: 0.5 }}>
            Contest type → category (Skill, Drill, Fitness, …) → individual skills with age benchmarks.
            This matches Frame 1 on{' '}
            <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
              /contests/{resolvedSport.toLowerCase()}
            </Typography>
            . Future types can appear as separate contest types here.
          </Typography>
        </Box>
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          sx={{ flexShrink: 0, minHeight: 40, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
        >
          <Tab label="Edit catalog" value="edit" />
          <Tab label="Categories" value="categories" />
          <Tab label="Grades" value="grades" />
          <Tab label="Journey preview" value="preview" />
        </Tabs>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {mainTab === 'preview' ? (
        <Paper variant="outlined" sx={{ p: { xs: 1, md: 1.5 }, borderRadius: 2, bgcolor: brandColors.ice }}>
          <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
            Preview reads skills from the database. Frame 2/3 use saved admin content when present, including age-wise
            copy and clips; JSON seed fills matching slugs until you save those frames. Re-open this tab or save a skill
            to refresh.
          </Alert>
          <ContestCatalogExperience
            key={previewRefreshKey}
            sportName={resolvedSport}
            catalogRefreshKey={previewRefreshKey}
            showCatalogSourceHint
          />
        </Paper>
      ) : mainTab === 'categories' ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: brandColors.navy }}>
                Categories — {contestTypeLabel}
              </Typography>
              <Typography variant="body2" sx={{ color: brandColors.slate, mt: 0.5 }}>
                Manage Skill, Drill, Fitness, and other groupings. The skill editor dropdown uses this list.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setNewCategory({
                  id: '',
                  label: '',
                  description: '',
                  targetType: 'skill',
                  sortOrder: categories.length + 1,
                });
                setCategoryCreateOpen(true);
              }}
              sx={{ textTransform: 'none', fontWeight: 700, bgcolor: brandColors.violet }}
            >
              Add category
            </Button>
          </Stack>

          <Box sx={{ overflowX: 'auto' }}>
            <Box
              component="table"
              sx={{
                width: '100%',
                minWidth: 720,
                borderCollapse: 'collapse',
                '& th, & td': { border: '1px solid #e2e8f0', p: 1, verticalAlign: 'top' },
                '& th': { bgcolor: '#f1f5f9', fontWeight: 700, fontSize: '0.85rem' },
              }}
            >
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Label</th>
                  <th>Description</th>
                  <th>Order</th>
                  <th>Target type</th>
                  <th>Active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => {
                  const row = categoryDrafts[c.id] || c;
                  return (
                    <tr key={c.id} style={{ opacity: row.isActive === false ? 0.55 : 1 }}>
                      <td>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {c.id}
                        </Typography>
                      </td>
                      <td>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.label || ''}
                          onChange={(e) =>
                            setCategoryDrafts((prev) => ({
                              ...prev,
                              [c.id]: { ...row, label: e.target.value },
                            }))
                          }
                        />
                      </td>
                      <td>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.description || ''}
                          onChange={(e) =>
                            setCategoryDrafts((prev) => ({
                              ...prev,
                              [c.id]: { ...row, description: e.target.value },
                            }))
                          }
                        />
                      </td>
                      <td>
                        <TextField
                          size="small"
                          type="number"
                          value={row.sortOrder ?? 0}
                          onChange={(e) =>
                            setCategoryDrafts((prev) => ({
                              ...prev,
                              [c.id]: { ...row, sortOrder: Number(e.target.value) || 0 },
                            }))
                          }
                          sx={{ width: 72 }}
                        />
                      </td>
                      <td>
                        <Select
                          size="small"
                          value={row.targetType || 'skill'}
                          onChange={(e) =>
                            setCategoryDrafts((prev) => ({
                              ...prev,
                              [c.id]: {
                                ...row,
                                targetType: e.target.value as CategoryDefinition['targetType'],
                              },
                            }))
                          }
                        >
                          <MenuItem value="skill">skill</MenuItem>
                          <MenuItem value="drill">drill</MenuItem>
                          <MenuItem value="fitness">fitness</MenuItem>
                        </Select>
                      </td>
                      <td>
                        <Checkbox
                          checked={row.isActive !== false}
                          onChange={(e) =>
                            setCategoryDrafts((prev) => ({
                              ...prev,
                              [c.id]: { ...row, isActive: e.target.checked },
                            }))
                          }
                        />
                      </td>
                      <td>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={saving}
                          onClick={() => handleSaveCategory(c.id)}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          Save
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Box>
          </Box>
        </Paper>
      ) : mainTab === 'grades' ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: brandColors.navy }}>
                Platform grades
              </Typography>
              <Typography variant="body2" sx={{ color: brandColors.slate, mt: 0.5 }}>
                Define performance grade bands used on scorecards and benchmark matrices for every contest skill.
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: brandColors.slate, mt: 0.75 }}>
                Initial grades were seeded once when the catalog database was set up. Delete unused grades here — they
                will not reappear automatically. Grades linked to skill benchmarks must be removed from those skills
                first, or uncheck Active to hide them.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={savingGrades || !gradesDirty}
                onClick={handleSaveAllGrades}
                sx={{ textTransform: 'none', fontWeight: 700, bgcolor: brandColors.navy }}
              >
                {savingGrades ? 'Saving…' : 'Save grades'}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={savingGrades}
                onClick={() => {
                  setNewGrade({
                    code: '',
                    label: '',
                    description: '',
                    sortOrder: grades.length + 1,
                  });
                  setGradeCreateOpen(true);
                }}
                sx={{ textTransform: 'none', fontWeight: 700, bgcolor: brandColors.violet }}
              >
                Add grade
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ overflowX: 'auto' }}>
            <Box
              component="table"
              sx={{
                width: '100%',
                minWidth: 680,
                borderCollapse: 'collapse',
                '& th, & td': { border: '1px solid #e2e8f0', p: 1, verticalAlign: 'top' },
                '& th': { bgcolor: '#f1f5f9', fontWeight: 700, fontSize: '0.85rem' },
              }}
            >
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Color</th>
                  <th>Label</th>
                  <th>Description</th>
                  <th>Order</th>
                  <th>Active</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {grades.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <Typography variant="body2" sx={{ color: brandColors.slate, py: 2, textAlign: 'center' }}>
                        No platform grades loaded. Add a grade or check that the backend catalog API is running.
                      </Typography>
                    </td>
                  </tr>
                ) : null}
                {grades.map((g) => {
                  const row = gradeDrafts[g.code] || g;
                  const dirty = gradeRowChanged(g, row);
                  return (
                    <tr
                      key={g.code}
                      style={{
                        opacity: row.isActive === false ? 0.55 : 1,
                        backgroundColor: dirty ? 'rgba(99, 102, 241, 0.06)' : undefined,
                      }}
                    >
                      <td>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.code ?? g.code}
                          onChange={(e) =>
                            setGradeDrafts((prev) => ({
                              ...prev,
                              [g.code]: { ...row, code: e.target.value },
                            }))
                          }
                          inputProps={{ style: { fontFamily: 'monospace', fontWeight: 700, fontSize: '0.85rem' } }}
                        />
                      </td>
                      <td>
                        {(() => {
                          const colors = getGradeColor(row.code ?? g.code);
                          return (
                            <Stack spacing={0.25} alignItems="flex-start">
                              <Box
                                sx={{
                                  width: 56,
                                  height: 22,
                                  borderRadius: 1,
                                  bgcolor: colors.bg,
                                  border: `1px solid ${colors.border}`,
                                }}
                                title={getGradeTierLabel(row.code ?? g.code)}
                              />
                              <Typography variant="caption" sx={{ color: brandColors.slate, lineHeight: 1.2 }}>
                                {colors.tier}
                              </Typography>
                            </Stack>
                          );
                        })()}
                      </td>
                      <td>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.label || ''}
                          onChange={(e) =>
                            setGradeDrafts((prev) => ({
                              ...prev,
                              [g.code]: { ...row, label: e.target.value },
                            }))
                          }
                        />
                      </td>
                      <td>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.description || ''}
                          onChange={(e) =>
                            setGradeDrafts((prev) => ({
                              ...prev,
                              [g.code]: { ...row, description: e.target.value },
                            }))
                          }
                        />
                      </td>
                      <td>
                        <TextField
                          size="small"
                          type="number"
                          value={row.sortOrder ?? 0}
                          onChange={(e) =>
                            setGradeDrafts((prev) => ({
                              ...prev,
                              [g.code]: { ...row, sortOrder: Number(e.target.value) || 0 },
                            }))
                          }
                          sx={{ width: 72 }}
                        />
                      </td>
                      <td>
                        <Checkbox
                          checked={row.isActive !== false}
                          onChange={(e) =>
                            setGradeDrafts((prev) => ({
                              ...prev,
                              [g.code]: { ...row, isActive: e.target.checked },
                            }))
                          }
                        />
                      </td>
                      <td>
                        <Tooltip title="Delete grade">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={savingGrades}
                              onClick={() => {
                                setGradeDeleteError(null);
                                setGradeToDelete(g);
                              }}
                              aria-label={`Delete grade ${g.code}`}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Box>
          </Box>
          {gradesDirty ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: brandColors.slate }}>
              Unsaved changes — click Save grades when you are done editing.
            </Typography>
          ) : null}
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '280px 1fr' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          {/* Skill list */}
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 1.5, bgcolor: brandColors.navy, color: '#fff' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Skills
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateOpen(true)}
                  sx={{ color: '#fff', textTransform: 'none', fontWeight: 600 }}
                >
                  Add
                </Button>
              </Stack>
            </Box>
            <Stack sx={{ maxHeight: 480, overflow: 'auto' }}>
              {loading && (
                <Typography variant="body2" sx={{ p: 2, color: brandColors.slate }}>
                  Loading…
                </Typography>
              )}
              {!loading && skills.length === 0 && (
                <Typography variant="body2" sx={{ p: 2, color: brandColors.slate }}>
                  No skills yet. Click Add to create the first {resolvedSport} skill.
                </Typography>
              )}
              {skills.map((s) => (
                <Box
                  key={s.id}
                  onClick={() => setSelectedId(s.id)}
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    cursor: 'pointer',
                    borderBottom: '1px solid #e2e8f0',
                    bgcolor: selectedId === s.id ? '#ede9fe' : 'transparent',
                    '&:hover': { bgcolor: selectedId === s.id ? '#ede9fe' : '#f8fafc' },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, color: brandColors.navy }}>
                    {s.title}
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={categoryLabelById.get(s.category) || s.category}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                    {s.difficultyLevel ? (
                      <Chip label={`Lv ${s.difficultyLevel}`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                    ) : null}
                    <CapabilityLevelChip level={s.capabilityLevel} compact />
                    {s.scope === 'global' && (
                      <Chip label="Global" size="small" color="secondary" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                    {!s.sportLink?.contestTaskId && s.scope === 'global' && (
                      <Chip
                        label="Not adopted"
                        size="small"
                        color="warning"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAdoptGlobal(s.id);
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Paper>

          {/* Editor */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            {!draft ? (
              <Typography variant="body2" sx={{ color: brandColors.slate }}>
                Select a skill from the list or add a new one.
              </Typography>
            ) : (
              <Stack spacing={2.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" sx={{ fontWeight: 700, color: brandColors.navy }}>
                    Edit skill
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={saving}
                    onClick={handleSave}
                    sx={{ textTransform: 'none', fontWeight: 700, bgcolor: brandColors.violet }}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </Stack>

                <Tabs
                  value={skillEditorTab}
                  onChange={(_, value) => setSkillEditorTab(value)}
                  sx={{ minHeight: 40, borderBottom: '1px solid #e2e8f0', '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 40 } }}
                >
                  <Tab label="Frame 1 — Skill & benchmarks" value="frame1" />
                  <Tab label="Frame 2 — Learn sections" value="frame2" />
                  <Tab label="Frame 3 — Videos" value="frame3" />
                </Tabs>

                {skillEditorTab === 'frame1' && (
                <>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Title"
                    fullWidth
                    required
                    value={draft.title || ''}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  />
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      label="Category"
                      value={draft.category || 'skill'}
                      onChange={(e) => setDraft({ ...draft, category: String(e.target.value) })}
                    >
                      {activeCategories.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Difficulty level
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Rating
                      value={draft.difficultyLevel ?? 0}
                      max={5}
                      onChange={(_, value) => setDraft({ ...draft, difficultyLevel: value || null })}
                    />
                    <Typography variant="body2" sx={{ color: brandColors.slate }}>
                      {draft.difficultyLevel ? `${draft.difficultyLevel}/5` : 'Not set'}
                    </Typography>
                  </Stack>
                </Box>

                <FormControl fullWidth>
                  <InputLabel>Capability Level</InputLabel>
                  <Select
                    label="Capability Level"
                    value={normalizeCapabilityLevel(draft.capabilityLevel)}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        capabilityLevel: e.target.value as ContestCapabilityLevel,
                      })
                    }
                  >
                    {CONTEST_CAPABILITY_LEVELS.map((level) => (
                      <MenuItem key={level} value={level}>
                        <CapabilityLevelChip level={level} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Subtitle"
                  fullWidth
                  value={draft.subtitle || ''}
                  onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })}
                />
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  minRows={2}
                  value={draft.description || ''}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                />

                <OptionChipList
                  label="Common mistakes (Error column)"
                  helperText="Players can select one or more of these in Self Evaluation only. Same pattern as Area of Improvement on Training/Ranking skills."
                  values={draft.commonMistakes || []}
                  onChange={(commonMistakes) => setDraft({ ...draft, commonMistakes })}
                />

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Enabled grades (visible on scorecard)
                  </Typography>
                  <FormGroup row>
                    {activeGrades.map((g) => (
                      <FormControlLabel
                        key={g.code}
                        control={
                          <Checkbox
                            checked={enabledGrades.includes(g.code)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setEnabledGrades((prev) => {
                                  const next = prev.includes(g.code) ? prev : [...prev, g.code];
                                  const order = new Map(
                                    activeGrades.map((row, i) => [row.code, row.sortOrder ?? i]),
                                  );
                                  return [...next].sort(
                                    (a, b) =>
                                      (order.get(a) ?? 999) - (order.get(b) ?? 999) ||
                                      a.localeCompare(b),
                                  );
                                });
                              } else {
                                setEnabledGrades((prev) => prev.filter((c) => c !== g.code));
                              }
                            }}
                          />
                        }
                        label={`${g.code} — ${g.label}`}
                      />
                    ))}
                  </FormGroup>
                </Box>

                <Divider />

                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Benchmarks — {resolvedSport}
                </Typography>
                <Typography variant="caption" sx={{ color: brandColors.slate, display: 'block', mb: 1 }}>
                  Dual Boy/Girl (or Male/Female for Senior/Para) targets per age — including target time.
                  Grade columns: min / max. Legacy shared values pre-fill both genders until you save.
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <Box
                    component="table"
                    sx={{
                      width: '100%',
                      minWidth: 920,
                      borderCollapse: 'collapse',
                      '& th, & td': { border: '1px solid #e2e8f0', p: 0.75, fontSize: '0.85rem' },
                      '& th': { bgcolor: '#f1f5f9', fontWeight: 700 },
                    }}
                  >
                    <thead>
                      <tr>
                        <th>Age</th>
                        <th>Gender</th>
                        <th>Target time</th>
                        <th>Time unit</th>
                        <th>Metric unit</th>
                        {orderedEnabledGrades.map((g) => (
                          <th key={g}>{g} (min–max)</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {CONTEST_AGE_CATEGORIES.map((age) =>
                        getContestGenderKeys(age).map((genderKey) => {
                          const ageRow = getAgeLimitRow(age, genderKey);
                          return (
                        <tr
                          key={`${age}-${genderKey}`}
                          style={{ background: getContestGenderAccent(genderKey).tint }}
                        >
                          <td>
                            <strong>{age}</strong>
                          </td>
                          <td>
                            <strong>{getContestGenderLabel(age, genderKey)}</strong>
                          </td>
                          <td>
                            <TextField
                              size="small"
                              placeholder="e.g. 2"
                              type="number"
                              value={ageRow?.recommendedMaxTime ?? ''}
                              onChange={(e) =>
                                syncAgeLimit(age, genderKey, {
                                  recommendedMaxTime:
                                    e.target.value === '' ? '' : Number(e.target.value),
                                })
                              }
                              sx={{ width: 72 }}
                              inputProps={{ style: { padding: '4px 6px', fontSize: '0.8rem' } }}
                            />
                          </td>
                          <td>
                            <TextField
                              size="small"
                              placeholder="min"
                              value={ageRow?.timeUnitLabel ?? 'min'}
                              onChange={(e) => syncAgeLimit(age, genderKey, { timeUnitLabel: e.target.value })}
                              sx={{ width: 64 }}
                              inputProps={{ style: { padding: '4px 6px', fontSize: '0.75rem' } }}
                            />
                          </td>
                          <td>
                            <TextField
                              size="small"
                              placeholder="smashes"
                              value={ageRow?.metricUnitLabel ?? ''}
                              onChange={(e) => syncAgeLimit(age, genderKey, { metricUnitLabel: e.target.value })}
                              inputProps={{ style: { padding: '4px 6px', fontSize: '0.75rem' } }}
                            />
                          </td>
                          {orderedEnabledGrades.map((gradeCode) => {
                            const cell = getBenchmarkCell(age, gradeCode, genderKey);
                            return (
                              <td key={`${genderKey}-${gradeCode}`}>
                                <Stack direction="row" spacing={0.5}>
                                  <TextField
                                    size="small"
                                    placeholder="min"
                                    type="number"
                                    value={cell?.minValue ?? ''}
                                    onChange={(e) =>
                                      syncBenchmarkCell(age, gradeCode, genderKey, {
                                        minValue: e.target.value === '' ? '' : Number(e.target.value),
                                      })
                                    }
                                    sx={{ width: 64 }}
                                    inputProps={{ style: { padding: '4px 6px', fontSize: '0.8rem' } }}
                                  />
                                  <TextField
                                    size="small"
                                    placeholder="max"
                                    type="number"
                                    value={cell?.maxValue ?? ''}
                                    onChange={(e) =>
                                      syncBenchmarkCell(age, gradeCode, genderKey, {
                                        maxValue: e.target.value === '' ? '' : Number(e.target.value),
                                      })
                                    }
                                    sx={{ width: 64 }}
                                    inputProps={{ style: { padding: '4px 6px', fontSize: '0.8rem' } }}
                                  />
                                </Stack>
                              </td>
                            );
                          })}
                        </tr>
                          );
                        }),
                      )}
                    </tbody>
                  </Box>
                </Box>

                {draft.sportLink?.contestTaskId && (
                  <Alert severity="success" sx={{ borderRadius: 2 }}>
                    Linked contest task: <code>{draft.sportLink.contestTaskId}</code> (video upload enabled)
                  </Alert>
                )}
                </>
                )}

                {skillEditorTab === 'frame2' && (
                  <Stack spacing={1.5}>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      Frame 2 is Skill and Performance Guidelines. The first accordion is always{' '}
                      <strong>Online Contest Liability Disclaimer &amp; Safety Rules</strong> (1.0). Hide unused
                      sections with Show on Learn — remaining headings keep consecutive numbers (1.0, 2.0, 3.0), so a
                      gap never appears. Blocks inside a heading are 2.0 / 2.1 when that heading is the second visible
                      section. Frame 3 videos use the same visible-section number.
                    </Alert>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {CONTEST_AGE_CATEGORIES.map((age) => {
                        const filled = (draft.sections || []).some((section) =>
                          sectionBlocksHaveContent(section.byAge?.[age]?.blocks) ||
                          String(section.byAge?.[age]?.content || '').trim(),
                        );
                        return (
                          <Chip
                            key={age}
                            label={age}
                            size="small"
                            color={frame2Age === age ? 'primary' : 'default'}
                            variant={frame2Age === age ? 'filled' : filled ? 'outlined' : 'outlined'}
                            onClick={() => setFrame2Age(age)}
                            sx={{ fontWeight: frame2Age === age ? 800 : 600, opacity: filled || frame2Age === age ? 1 : 0.7 }}
                          />
                        );
                      })}
                    </Stack>
                    {frame2Age !== 'U13' && (
                      <Button
                        size="small"
                        onClick={() => {
                          const current = draft.sections?.length ? draft.sections : emptyLearnSections();
                          setDraft({
                            ...draft,
                            sections: current.map((section) =>
                              setAgeSection(section, frame2Age, {
                                blocks: hydrateSectionBlocks(
                                  section.byAge?.U13 || {
                                    content: section.content,
                                    bullets: section.bullets,
                                    blocks: section.blocks,
                                  },
                                ),
                              }),
                            ),
                          });
                        }}
                        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                      >
                        Copy U13 into {frame2Age}
                      </Button>
                    )}
                    {CONTEST_SECTION_ORDER.map(({ key, label }) => {
                      const section =
                        (draft.sections || []).find((row) => row.key === key) || {
                          key,
                          label,
                          content: '',
                          bullets: [],
                        };
                      const required = isRequiredFrame2Section(key);
                      const hidden = required ? false : Boolean(section.hidden);
                      const visibleIndex = getFrame2SectionVisibleIndex(
                        { sections: draft.sections || [] },
                        key,
                      );
                      const sectionNumber = hidden ? 'Hidden' : formatContestVideoNumber(visibleIndex);
                      const byAge = hydrateSectionByAge(section);
                      const stored = byAge[frame2Age];
                      const hasOwn =
                        sectionBlocksHaveContent(stored.blocks) || String(stored.content || '').trim();
                      const editing = hasOwn
                        ? emptyAgeSection(stored, {
                            content: section.content,
                            bullets: section.bullets,
                            blocks: section.blocks,
                          })
                        : emptyAgeSection(undefined, {
                            content: section.content,
                            bullets: section.bullets,
                            blocks: section.blocks,
                          });
                      const currentSections = draft.sections?.length ? draft.sections : emptyLearnSections();
                      const updateBlocks = (blocks: ContestSectionBlock[]) => {
                        setDraft({
                          ...draft,
                          sections: CONTEST_SECTION_ORDER.map((meta) => {
                            const existing =
                              currentSections.find((row) => row.key === meta.key) || {
                                key: meta.key,
                                label: meta.label,
                                content: '',
                                bullets: [],
                              };
                            return meta.key === key ? setAgeSection(existing, frame2Age, { blocks }) : existing;
                          }),
                        });
                      };
                      const updateHidden = (nextHidden: boolean) => {
                        setDraft({
                          ...draft,
                          sections: CONTEST_SECTION_ORDER.map((meta) => {
                            const existing =
                              currentSections.find((row) => row.key === meta.key) || {
                                key: meta.key,
                                label: meta.label,
                                content: '',
                                bullets: [],
                              };
                            return meta.key === key
                              ? { ...existing, hidden: required ? false : nextHidden }
                              : existing;
                          }),
                        });
                      };
                      return (
                        <Accordion key={key} defaultExpanded={key === 'liability_disclaimer'} disableGutters>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ width: '100%', pr: 1, gap: 1 }}
                            >
                              <Typography sx={{ fontWeight: 700, opacity: hidden ? 0.55 : 1 }}>
                                {sectionNumber} {label}
                              </Typography>
                              <FormControlLabel
                                onClick={(event) => event.stopPropagation()}
                                onFocus={(event) => event.stopPropagation()}
                                control={
                                  <Switch
                                    size="small"
                                    checked={!hidden}
                                    disabled={required}
                                    onChange={(event) => updateHidden(!event.target.checked)}
                                  />
                                }
                                label={required ? 'Always on' : 'Show on Learn'}
                                sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                              />
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails>
                            {!hasOwn && (section.content || sectionBlocksHaveContent(section.blocks)) ? (
                              <Typography variant="caption" sx={{ color: brandColors.slate, display: 'block', mb: 1 }}>
                                Showing U13/shared copy for {frame2Age}. Edit to save an age-specific version.
                              </Typography>
                            ) : null}
                            <AdminFrame2BlockEditor
                              blocks={editing.blocks || []}
                              onChange={updateBlocks}
                              sectionIndex={hidden ? 0 : visibleIndex}
                            />
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Stack>
                )}

                {skillEditorTab === 'frame3' && (
                  <Stack spacing={1.5}>
                    {(() => {
                      const sectionVideos = (draft.media || []).filter(
                        (item) => (item.sectionKey || 'liability_disclaimer') === frame3SectionKey,
                      );
                      const sectionHidden = Boolean(
                        (draft.sections || []).find((row) => row.key === frame3SectionKey)?.hidden,
                      ) && !isRequiredFrame2Section(frame3SectionKey);
                      const sectionMajor = sectionHidden
                        ? 0
                        : getFrame2SectionVisibleIndex(
                            { sections: draft.sections || [] },
                            frame3SectionKey,
                          );
                      const frame3Number = (index: number, relatedIndex?: number) =>
                        sectionHidden
                          ? relatedIndex == null
                            ? `Hidden ${index + 1}`
                            : `Hidden ${index + 1}.${relatedIndex + 1}`
                          : formatContestVideoNumber(sectionMajor + index, relatedIndex);
                      const sectionLabel =
                        CONTEST_SECTION_ORDER.find((section) => section.key === frame3SectionKey)?.label ||
                        frame3SectionKey;
                      const updateMedia = (id: string, patch: Partial<ContestMediaItem>) =>
                        setDraft({
                          ...draft,
                          media: (draft.media || []).map((row) => (row.id === id ? { ...row, ...patch } : row)),
                        });
                      const updateMediaAge = (
                        id: string,
                        age: string,
                        patch: Partial<{ title: string; url: string; notes: string }>,
                      ) =>
                        setDraft({
                          ...draft,
                          media: (draft.media || []).map((row) => (row.id === id ? setAgeClip(row, age, patch) : row)),
                        });
                      return (
                        <>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      Frame 3 videos follow the selected Frame 2 heading and use that heading&apos;s visible number. If
                      Scoring is 2.0 on Learn, its clips are 2.0 / 2.1. Hiding a Frame 2 section drops it from Learn
                      and the next heading (and its videos) move up so numbering stays consecutive.
                    </Alert>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Frame 2 section</InputLabel>
                        <Select
                          label="Frame 2 section"
                          value={frame3SectionKey}
                          onChange={(e) => setFrame3SectionKey(e.target.value as ContestSectionKey)}
                        >
                          {CONTEST_SECTION_ORDER.map((section) => {
                            const count = (draft.media || []).filter(
                              (item) => (item.sectionKey || 'liability_disclaimer') === section.key,
                            ).length;
                            const hidden =
                              Boolean((draft.sections || []).find((row) => row.key === section.key)?.hidden) &&
                              !isRequiredFrame2Section(section.key);
                            const number = hidden
                              ? 'Hidden'
                              : formatContestVideoNumber(
                                  getFrame2SectionVisibleIndex({ sections: draft.sections || [] }, section.key),
                                );
                            return (
                              <MenuItem key={section.key} value={section.key}>
                                {number} {section.label}
                                {count ? ` (${count})` : ''}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() =>
                          setDraft({
                            ...draft,
                            media: [...(draft.media || []), newMediaItem(frame3SectionKey)],
                          })
                        }
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                      >
                        Add {frame3Number(sectionVideos.length)}
                      </Button>
                    </Stack>
                    {sectionVideos.length === 0 && (
                      <Typography variant="body2" sx={{ color: brandColors.slate }}>
                        No videos for {sectionLabel} yet. Add {frame3Number(0)} for this section,
                        or pick another Frame 2 heading
                        {sectionHidden ? ' (this heading is hidden on Learn).' : '.'}
                      </Typography>
                    )}
                    {sectionVideos.map((item, index) => {
                      const numberLabel = frame3Number(index);
                      const related = item.relatedVideos || [];
                      return (
                      <Paper key={item.id || `media-${index}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Stack spacing={1.25}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {numberLabel} · {sectionLabel}
                            </Typography>
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={`Remove video ${numberLabel}`}
                              onClick={() =>
                                setDraft({
                                  ...draft,
                                  media: (draft.media || []).filter((row) => row.id !== item.id),
                                })
                              }
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                          <FormControl fullWidth size="small">
                            <InputLabel>Role</InputLabel>
                            <Select
                              label="Role"
                              value={item.role || 'primary'}
                              onChange={(e) =>
                                updateMedia(item.id, { role: e.target.value as ContestMediaItem['role'] })
                              }
                            >
                              <MenuItem value="primary">Primary</MenuItem>
                              <MenuItem value="reference">Reference</MenuItem>
                              <MenuItem value="sample">Sample</MenuItem>
                            </Select>
                          </FormControl>
                          <AgeClipTable
                            item={item}
                            onChangeAge={(age, patch) => updateMediaAge(item.id, age, patch)}
                          />
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" sx={{ fontWeight: 700, color: brandColors.slate }}>
                              Related videos ({frame3Number(index, 0)}, {frame3Number(index, 1)}, …)
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() =>
                                updateMedia(item.id, {
                                  relatedVideos: [...related, newRelatedVideo()],
                                })
                              }
                              sx={{ textTransform: 'none' }}
                            >
                              Add {frame3Number(index, related.length)}
                            </Button>
                          </Stack>
                          {related.map((rel, relatedIndex) => {
                            const relatedNumber = frame3Number(index, relatedIndex);
                            return (
                              <Paper key={rel.id || relatedNumber} variant="outlined" sx={{ p: 1.25, bgcolor: '#f8fafc' }}>
                                <Stack spacing={1}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="caption" sx={{ fontWeight: 800 }}>
                                      {relatedNumber}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      aria-label={`Remove ${relatedNumber}`}
                                      onClick={() =>
                                        updateMedia(item.id, {
                                          relatedVideos: related.filter((row) => row.id !== rel.id),
                                        })
                                      }
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                  <AgeClipTable
                                    item={rel}
                                    onChangeAge={(age, patch) =>
                                      updateMedia(item.id, {
                                        relatedVideos: related.map((row) =>
                                          row.id === rel.id ? setAgeClip(row, age, patch) : row,
                                        ),
                                      })
                                    }
                                  />
                                </Stack>
                              </Paper>
                            );
                          })}
                        </Stack>
                      </Paper>
                      );
                    })}
                        </>
                      );
                    })()}
                  </Stack>
                )}
              </Stack>
            )}
          </Paper>
        </Box>
      )}

      <Dialog open={gradeCreateOpen} onClose={() => setGradeCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add platform grade</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Code"
              required
              fullWidth
              helperText="Short identifier shown on scorecards (letters, numbers, or symbols)."
              value={newGrade.code}
              onChange={(e) => setNewGrade({ ...newGrade, code: e.target.value })}
            />
            <TextField
              label="Label"
              required
              fullWidth
              value={newGrade.label}
              onChange={(e) => setNewGrade({ ...newGrade, label: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={newGrade.description}
              onChange={(e) => setNewGrade({ ...newGrade, description: e.target.value })}
            />
            <TextField
              label="Sort order"
              type="number"
              fullWidth
              value={newGrade.sortOrder}
              onChange={(e) =>
                setNewGrade({ ...newGrade, sortOrder: Number(e.target.value) || 0 })
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGradeCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={savingGrades || !newGrade.code.trim() || !newGrade.label.trim()}
            onClick={handleCreateGrade}
          >
            Create grade
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(gradeToDelete)}
        onClose={() => {
          setGradeToDelete(null);
          setGradeDeleteError(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove grade?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2" sx={{ color: brandColors.slate }}>
              Remove grade{' '}
              <strong>
                {gradeToDelete?.code} — {gradeToDelete?.label}
              </strong>
              ?
            </Typography>
            <Typography variant="caption" sx={{ color: brandColors.slate }}>
              <strong>Hide grade</strong> unchecks the Active checkbox (same as clearing Active in the table and
              clicking Save grades). Use this when the grade is still linked to skill benchmarks.{' '}
              <strong>Delete permanently</strong> removes the row entirely when nothing references it.
            </Typography>
            {gradeDeleteError ? (
              <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
                {gradeDeleteError}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setGradeToDelete(null);
              setGradeDeleteError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            disabled={savingGrades}
            onClick={() => handleDeleteGrade(true)}
          >
            Hide grade
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={savingGrades}
            onClick={() => handleDeleteGrade(false)}
          >
            Delete permanently
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={categoryCreateOpen} onClose={() => setCategoryCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add category — {contestTypeLabel}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Label"
              required
              fullWidth
              value={newCategory.label}
              onChange={(e) => setNewCategory({ ...newCategory, label: e.target.value })}
            />
            <TextField
              label="ID (optional slug)"
              fullWidth
              helperText="Leave blank to auto-generate from label, e.g. mental-skills"
              value={newCategory.id}
              onChange={(e) => setNewCategory({ ...newCategory, id: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={newCategory.description}
              onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Target type</InputLabel>
              <Select
                label="Target type"
                value={newCategory.targetType}
                onChange={(e) =>
                  setNewCategory({
                    ...newCategory,
                    targetType: e.target.value as CategoryDefinition['targetType'],
                  })
                }
              >
                <MenuItem value="skill">skill</MenuItem>
                <MenuItem value="drill">drill</MenuItem>
                <MenuItem value="fitness">fitness</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Sort order"
              type="number"
              fullWidth
              value={newCategory.sortOrder}
              onChange={(e) =>
                setNewCategory({ ...newCategory, sortOrder: Number(e.target.value) || 0 })
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving || !newCategory.label.trim()}
            onClick={handleCreateCategory}
          >
            Create category
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add skill — {contestTypeLabel}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              required
              fullWidth
              value={newSkill.title || ''}
              onChange={(e) => setNewSkill({ ...newSkill, title: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={newSkill.category || 'skill'}
                onChange={(e) => setNewSkill({ ...newSkill, category: String(e.target.value) })}
              >
                {activeCategories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Difficulty level
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Rating
                  value={newSkill.difficultyLevel ?? 0}
                  max={5}
                  onChange={(_, value) => setNewSkill({ ...newSkill, difficultyLevel: value || 1 })}
                />
                <Typography variant="body2" sx={{ color: brandColors.slate }}>
                  {(newSkill.difficultyLevel ?? 1)}/5
                </Typography>
              </Stack>
            </Box>
            <FormControl fullWidth>
              <InputLabel>Capability Level</InputLabel>
              <Select
                label="Capability Level"
                value={normalizeCapabilityLevel(newSkill.capabilityLevel)}
                onChange={(e) =>
                  setNewSkill({
                    ...newSkill,
                    capabilityLevel: e.target.value as ContestCapabilityLevel,
                  })
                }
              >
                {CONTEST_CAPABILITY_LEVELS.map((level) => (
                  <MenuItem key={level} value={level}>
                    <CapabilityLevelChip level={level} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Scope</InputLabel>
              <Select
                label="Scope"
                value={newSkill.scope || 'sport_specific'}
                onChange={(e) =>
                  setNewSkill({
                    ...newSkill,
                    scope: e.target.value as 'global' | 'sport_specific',
                  })
                }
              >
                <MenuItem value="sport_specific">{resolvedSport} only</MenuItem>
                <MenuItem value="global">Global (all sports — adopt per sport)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={newSkill.description || ''}
              onChange={(e) => setNewSkill({ ...newSkill, description: e.target.value })}
            />
            <OptionChipList
              label="Common mistakes (Error column)"
              helperText="Optional now — you can also add these after creating the skill."
              values={newSkill.commonMistakes || []}
              onChange={(commonMistakes) => setNewSkill({ ...newSkill, commonMistakes })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={saving || !newSkill.title?.trim()} onClick={handleCreate}>
            Create + link task
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminContestCatalog;
